import createDOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { marked } from 'marked';

/** Согласовано с каноном rich_text sanitize (без script/style/on*) */
const SANITIZE: Config = {
  RETURN_TRUSTED_TYPE: false,
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strong',
    'sub',
    'sup',
    'u',
    'ul'
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class', 'id']
};

marked.setOptions({ gfm: true, breaks: true });

/** Slug заголовка для якорей внутри документа: те же правила, что у `scripts/docs_check.py`. */
export function slugifyMarkdownHeading(title: string): string {
  const t = title.trim().toLowerCase().replace(/\s+/g, '-');
  return t.replace(/[^-a-z0-9а-яё]/g, '') || 'section';
}

/** Проставляет `id` заголовкам, чтобы работали ссылки вида `#раздел`. */
function addHeadingIds(html: string): string {
  return html.replace(/<h([1-6])>(.*?)<\/h\1>/g, (_match, level: string, inner: string) => {
    const plain = inner.replace(/<[^>]*>/g, '');
    return `<h${level} id="${slugifyMarkdownHeading(plain)}">${inner}</h${level}>`;
  });
}

export type MarkdownToSafeHtmlOptions = {
  /** Добавлять `id` заголовкам (для навигации по якорям на странице документации). */
  headingIds?: boolean;
};

let purifier: ReturnType<typeof createDOMPurify> | null = null;

function getPurifier(): ReturnType<typeof createDOMPurify> {
  if (purifier && typeof purifier.sanitize === 'function' && purifier.isSupported) {
    return purifier;
  }
  const w = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
  if (!w?.document) {
    throw new Error('markdownToSafeHtml: требуется окружение с DOM (window.document)');
  }
  purifier = createDOMPurify(w);
  if (!purifier.isSupported || typeof purifier.sanitize !== 'function') {
    throw new Error('markdownToSafeHtml: DOMPurify недоступен в этом окружении');
  }
  return purifier;
}

export function markdownToSafeHtml(markdown: string, options?: MarkdownToSafeHtmlOptions): string {
  const raw = marked.parse(markdown || '', { async: false }) as string;
  const prepared = options?.headingIds ? addHeadingIds(raw) : raw;
  return getPurifier().sanitize(prepared, SANITIZE);
}
